import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../services/api';
import { useHapticFeedback } from '../hooks/useTelegram';
import { useTranslation } from '../i18n';
import { ymGoal } from '../utils/ym';
import type { OnboardingStepData } from '../types';
import './OnboardingPage.css';

interface OnboardingPageProps {
  onComplete: () => void;
}

const OnboardingPage: React.FC<OnboardingPageProps> = ({ onComplete }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [onboardingData, setOnboardingData] = useState<OnboardingStepData>({});
  const [loading, setLoading] = useState(false);
  const { notification } = useHapticFeedback();
  const navigate = useNavigate();
  const t = useTranslation();

  const totalSteps = 5;

  const handleNext = useCallback(() => {
    if (currentStep < totalSteps) {
      setCurrentStep(prev => prev + 1);
    }
  }, [currentStep, totalSteps]);

  const handleBack = useCallback(() => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
    }
  }, [currentStep]);



  const handleCompleteAndNavigate = useCallback(async () => {
    try {
      setLoading(true);
      await apiClient.completeOnboarding(false, onboardingData);

      // Цель top.mail.ru (VK Ads): онбординг пройден (Соня 28.07).
      try {
        const w = window as any;
        w._tmr = w._tmr || [];
        w._tmr.push({ type: 'reachGoal', id: 3783047, goal: 'oprosdone' });
      } catch (e) { /* нет счётчика — не критично */ }

      // Цель Яндекс.Метрики: завершение онбординга (Соня 29.07).
      ymGoal('quiz-complete');

      notification('success');
      navigate('/');
      onComplete();
    } catch (error) {
      console.error('Failed to complete onboarding:', error);
      notification('error');
    } finally {
      setLoading(false);
    }
  }, [onComplete, notification, navigate, onboardingData]);

  const handleSkipOnboarding = useCallback(async () => {
    try {
      setLoading(true);
      await apiClient.completeOnboarding(true);
      
      notification('success');
      onComplete();
    } catch (error) {
      console.error('Failed to skip onboarding:', error);
      notification('error');
    } finally {
      setLoading(false);
    }
  }, [onComplete, notification]);

  const updateOnboardingData = useCallback((data: Partial<OnboardingStepData>) => {
    setOnboardingData(prev => ({ ...prev, ...data }));
  }, []);

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return <Step1 onNext={handleNext} onSkip={handleSkipOnboarding} loading={loading} t={t} />;
      case 2:
        return <Step2 onNext={handleNext} onBack={handleBack} onUpdateData={updateOnboardingData} onSkip={handleSkipOnboarding} loading={loading} t={t} />;
      case 3:
        return <Step3 onNext={handleNext} onBack={handleBack} onUpdateData={updateOnboardingData} onSkip={handleSkipOnboarding} loading={loading} t={t} />;
      case 4:
        return <Step4 onNext={handleNext} onBack={handleBack} onUpdateData={updateOnboardingData} onSkip={handleSkipOnboarding} loading={loading} t={t} />;
      case 5:
        return <Step5 onCompleteAndNavigate={handleCompleteAndNavigate} loading={loading} t={t} />;
      default:
        return null;
    }
  };

  return (
    <div className="onboarding-page">
      <div className="progress-bar">
        <div 
          className="progress-fill" 
          style={{ width: `${(currentStep / totalSteps) * 100}%` }}
        />
      </div>
      <div className="onboarding-content">
        {renderStep()}
      </div>
    </div>
  );
};

// Step 1: Welcome
const Step1: React.FC<{ 
  onNext: () => void; 
  onSkip: () => void; 
  loading: boolean;
  t: any;
}> = ({ onNext, onSkip, loading, t }) => {
  return (
    <div className="onboarding-step first-step">
      <div className="step-header">
        <div className="robot-image">
          <img src="/exercises.png" alt="Calmaro Exercises" width="100" />
        </div>
        <h2 className="step-title">
          {t.onboarding.welcomeTitle.split('\n').map((line: string, i: number) => (
            <React.Fragment key={i}>{line}{i === 0 && <br />}</React.Fragment>
          ))}
        </h2>
        <p className="step-subtitle">
          {t.onboarding.welcomeSubtitle}
        </p>
      </div>
      <div className="step-footer">
        <button className="continue-button" onClick={onNext} disabled={loading}>
          {t.common.continue}
        </button>
        <button className="skip-button" onClick={onSkip} disabled={loading}>
          {t.common.skip}
        </button>
      </div>
    </div>
  );
};

// Step 2: Meditation experience
const Step2: React.FC<{ 
  onNext: () => void; 
  onBack: () => void;
  onUpdateData: (data: Partial<OnboardingStepData>) => void; 
  onSkip: () => void; 
  loading: boolean;
  t: any;
}> = ({ onNext, onBack, onUpdateData, onSkip, loading, t }) => {
  const [selectedExperience, setSelectedExperience] = useState<string>('');

  const experiences = [
    { id: 'beginner', label: t.onboarding.beginner, icon: '🌱', description: t.onboarding.beginnerDesc },
    { id: 'intermediate', label: t.onboarding.intermediate, icon: '🌿', description: t.onboarding.intermediateDesc },
    { id: 'advanced', label: t.onboarding.advanced, icon: '🌳', description: t.onboarding.advancedDesc },
  ];

  const handleExperienceSelect = (experienceId: string) => {
    setSelectedExperience(experienceId);
    onUpdateData({ meditation_experience: experienceId });
  };

  const handleNext = () => {
    if (selectedExperience) {
      onNext();
    }
  };

  return (
    <div className="onboarding-step">
      <div className="step-header">
        <div className="step-header-top">
          <button className="back-arrow" onClick={onBack}>
            <img src="/back.svg" alt={t.common.back} className="back-icon" />
          </button>
          <h2 className="step-title">{t.onboarding.experienceTitle}</h2>
          <div className="step-header-spacer"></div>
        </div>
        <p className="step-subtitle">
          {t.onboarding.experienceSubtitle}
        </p>
      </div>
      <div className="step-body">
        <div className="communication-options">
          {experiences.map(exp => (
            <div
              key={exp.id}
              className={`communication-option experience-option ${selectedExperience === exp.id ? 'selected' : ''}`}
              onClick={() => handleExperienceSelect(exp.id)}
            >
              <div className="experience-icon">{exp.icon}</div>
              <div className="experience-label">{exp.label}</div>
              <div className="experience-description">{exp.description}</div>
            </div>
          ))}
        </div>
      </div>
      <div className="step-footer">
        <button 
          className="continue-button" 
          onClick={handleNext}
          disabled={!selectedExperience || loading}
        >
          {t.common.continue}
        </button>
        <button className="skip-button" onClick={onSkip} disabled={loading}>
          {t.common.skip}
        </button>
      </div>
    </div>
  );
};

// Step 3: Meditation goals
const Step3: React.FC<{ 
  onNext: () => void; 
  onBack: () => void;
  onUpdateData: (data: Partial<OnboardingStepData>) => void; 
  onSkip: () => void; 
  loading: boolean;
  t: any;
}> = ({ onNext, onBack, onUpdateData, onSkip, loading, t }) => {
  const [selectedGoals, setSelectedGoals] = useState<string[]>([]);

  const goals = [
    { id: 'reduce_stress', label: t.onboarding.reduceStress, icon: '🪷' },
    { id: 'improve_sleep', label: t.onboarding.improveSleep, icon: '🌙' },
    { id: 'increase_focus', label: t.onboarding.increaseFocus, icon: '🍃' },
    { id: 'manage_emotions', label: t.onboarding.manageEmotions, icon: '🕊️' },
    { id: 'find_peace', label: t.onboarding.findPeace, icon: '☮️' },
  ];

  const handleGoalToggle = (goalId: string) => {
    const newGoals = selectedGoals.includes(goalId)
      ? selectedGoals.filter(id => id !== goalId)
      : [...selectedGoals, goalId];
    
    setSelectedGoals(newGoals);
    onUpdateData({ meditation_goals: newGoals });
  };

  const handleNext = () => {
    if (selectedGoals.length > 0) {
      onNext();
    }
  };

  return (
    <div className="onboarding-step">
      <div className="step-header">
        <div className="step-header-top">
          <button className="back-arrow" onClick={onBack}>
            <img src="/back.svg" alt={t.common.back} className="back-icon" />
          </button>
          <h2 className="step-title">{t.onboarding.goalsTitle}</h2>
          <div className="step-header-spacer"></div>
        </div>
        <p className="step-subtitle">
          {t.onboarding.goalsSubtitle}
        </p>
      </div>
      <div className="step-body">
        <div className="options-grid">
          {goals.map(goal => (
            <div
              key={goal.id}
              className={`option-item ${selectedGoals.includes(goal.id) ? 'selected' : ''}`}
              onClick={() => handleGoalToggle(goal.id)}
            >
              <div className="option-icon">{goal.icon}</div>
              <div className="option-text">{goal.label}</div>
            </div>
          ))}
        </div>
      </div>
      <div className="step-footer">
        <button 
          className="continue-button" 
          onClick={handleNext}
          disabled={selectedGoals.length === 0 || loading}
        >
          {t.common.continue}
        </button>
        <button className="skip-button" onClick={onSkip} disabled={loading}>
          {t.common.skip}
        </button>
      </div>
    </div>
  );
};

// Step 4: Meditation types and duration
const Step4: React.FC<{ 
  onNext: () => void; 
  onBack: () => void;
  onUpdateData: (data: Partial<OnboardingStepData>) => void; 
  onSkip: () => void; 
  loading: boolean;
  t: any;
}> = ({ onNext, onBack, onUpdateData, onSkip, loading, t }) => {
  const [selectedTypes, setSelectedTypes] = useState<string[]>([]);
  const [selectedDuration, setSelectedDuration] = useState<number | null>(null);

  const types = [
    { id: 'mindfulness', label: t.onboarding.mindfulness, icon: '🪷' },
    { id: 'breathing', label: t.onboarding.breathingPractices, icon: '🌬️' },
    { id: 'visualization', label: t.onboarding.visualization, icon: '🌅' },
    { id: 'mantras', label: t.onboarding.mantras, icon: '🕉️' },
    { id: 'body_scan', label: t.onboarding.bodyScan, icon: '✨' },
  ];

  const durations = [
    { value: 5, label: `5 ${t.common.minutes}` },
    { value: 10, label: `10 ${t.common.minutes}` },
    { value: 15, label: `15 ${t.common.minutes}` },
    { value: 20, label: `20 ${t.common.minutes}` },
    { value: 30, label: `30+ ${t.common.minutes}` },
  ];

  const handleTypeToggle = (typeId: string) => {
    const newTypes = selectedTypes.includes(typeId)
      ? selectedTypes.filter(id => id !== typeId)
      : [...selectedTypes, typeId];
    
    setSelectedTypes(newTypes);
    onUpdateData({ meditation_types: newTypes });
  };

  const handleDurationSelect = (duration: number) => {
    setSelectedDuration(duration);
    onUpdateData({ meditation_duration_preference: duration });
  };

  const handleNext = () => {
    if (selectedTypes.length > 0 && selectedDuration) {
      onNext();
    }
  };

  return (
    <div className="onboarding-step">
      <div className="step-header">
        <div className="step-header-top">
          <button className="back-arrow" onClick={onBack}>
            <img src="/back.svg" alt={t.common.back} className="back-icon" />
          </button>
          <h2 className="step-title">{t.onboarding.preferencesTitle}</h2>
          <div className="step-header-spacer"></div>
        </div>
        <p className="step-subtitle">
          {t.onboarding.preferencesSubtitle}
        </p>
      </div>
      <div className="step-body">
        <div className="preferences-section">
          <h3 className="preferences-section-title">{t.onboarding.meditationTypes}</h3>
          <div className="options-grid">
            {types.map(type => (
              <div
                key={type.id}
                className={`option-item ${selectedTypes.includes(type.id) ? 'selected' : ''}`}
                onClick={() => handleTypeToggle(type.id)}
              >
                <div className="option-icon">{type.icon}</div>
                <div className="option-text">{type.label}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="preferences-section" style={{ marginTop: '24px' }}>
          <h3 className="preferences-section-title">{t.onboarding.sessionDuration}</h3>
          <div className="communication-options">
            {durations.map(duration => (
              <div
                key={duration.value}
                className={`communication-option ${selectedDuration === duration.value ? 'selected' : ''}`}
                onClick={() => handleDurationSelect(duration.value)}
              >
                <div className="communication-text">{duration.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="step-footer">
        <button 
          className="continue-button" 
          onClick={handleNext}
          disabled={selectedTypes.length === 0 || !selectedDuration || loading}
        >
          {t.common.continue}
        </button>
        <button className="skip-button" onClick={onSkip} disabled={loading}>
          {t.common.skip}
        </button>
      </div>
    </div>
  );
};

// Step 5: Completion with reward
const Step5: React.FC<{ 
  onCompleteAndNavigate: () => Promise<void>; 
  loading: boolean;
  t: any;
}> = ({ onCompleteAndNavigate, loading, t }) => {
  return (
    <div className="onboarding-step reward-step">
      <div className="step-header">
        <h2 className="step-title reward-title">{t.onboarding.congratulations}</h2>
        <div className="reward-amount">+10 🌻</div>
        <p className="step-subtitle">
          {t.onboarding.rewardReceived}
        </p>
        <p className="step-subtitle">
          {t.onboarding.continueEarning}
        </p>
      </div>
      <div className="step-footer">
        <button 
          className="continue-button" 
          onClick={onCompleteAndNavigate}
          disabled={loading}
        >
          {loading ? t.common.loading : t.onboarding.startJourney}
        </button>
      </div>
    </div>
  );
};

export default OnboardingPage;
